import { createFileRoute } from "@tanstack/react-router";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { usePostVerdict } from "@/hooks/use-post-verdict";
import { NanaeHeader, NanaeFooter } from "@/components/qv3";
import { PostVerdictRecapCard } from "@/components/qorum/PostVerdictRecap";
import { SpokespersonPresence } from "@/components/qorum/SpokespersonPresence";
import { PostVerdictThread } from "@/components/qorum/PostVerdictThread";
import { PostVerdictInput } from "@/components/qorum/PostVerdictInput";
import { PostVerdictSecondaryActions } from "@/components/qorum/PostVerdictSecondaryActions";
import { socialMeta } from "@/lib/seo-meta";

export const Route = createFileRoute("/post-verdict/$id")({
  head: ({ params }) => ({
    meta: [{ title: "Le Dialogue — Hōjin" }, { name: "robots", content: "noindex, nofollow" },
      ...socialMeta({
        title: "Le Dialogue — Hōjin",
        description: "Le Dialogue — Hōjin",
        url: `https://hojin.ai/post-verdict/${params.id}`,
        image: "memo",
        card: "summary_large_image",
      }),
    ],
    links: [{ rel: "canonical", href: `https://hojin.ai/post-verdict/${params.id}` }],
  }),
  component: PostVerdictPage,
});

export function PostVerdictPage() {
  const { id } = Route.useParams();
  return (
    <ProtectedRoute>
      <PostVerdictView id={id} />
    </ProtectedRoute>
  );
}

export function PostVerdictView({ id }: { id: string }) {
  const state = usePostVerdict(id);

  return (
    <div
      data-testid="post-verdict-page"
      style={{
        background: "var(--paper)",
        minHeight: "100vh",
        paddingBottom: 100,
        position: "relative",
      }}
    >
      <NanaeHeader variant="minimal" title={`Verdict · ${id.slice(0, 8)}`} />

      <section
        aria-label="Dialogue avec le porte-parole"
        style={{
          maxWidth: 720,
          margin: "0 auto",
          padding: "40px 32px 0",
        }}
      >
        {state.isLoading || !state.verdict || !state.spokesperson ? (
          <div
            role="status"
            aria-live="polite"
            style={{
              fontFamily: '"DM Mono", monospace',
              fontSize: 11,
              letterSpacing: 2,
              textTransform: "uppercase",
              color: "var(--smoke)",
              textAlign: "center",
              padding: "80px 0",
            }}
          >
            {state.error ?? (state.isLoading ? "Le porte-parole rejoint la séance…" : "Verdict introuvable")}
          </div>
        ) : (
          <>
            <PostVerdictRecapCard recap={state.verdict} />
            <SpokespersonPresence spokesperson={state.spokesperson} />
            <PostVerdictThread thread={state.thread} spokesperson={state.spokesperson} />
            <PostVerdictSecondaryActions verdictId={id} />
          </>
        )}
      </section>

      {state.spokesperson ? (
        <PostVerdictInput onSend={state.sendMessage} isSending={state.isSending} />
      ) : null}

      <NanaeFooter variant="minimal" />
    </div>
  );
}

export default PostVerdictPage;
